import { Container } from '@design-system/container';
import { ExtendableImage } from '@design-system/extendable-image';
import { Section } from '@design-system/section';
import { Typography } from '@design-system/typography';
import { graphql, useStaticQuery } from 'gatsby';
import React, { ReactElement } from 'react';

import TMPLinkWrap from '~/components/TMPLinkWrap';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { useCurrentLang } from '~/hooks/useCurrentLang';
import { Layout } from '~/layout/Layout';
import { imageMapper } from '~/mappers/image';
import { linkMapper } from '~/mappers/link/linkMapper';
import { typographyMapper } from '~/mappers/typography';

import { NotFoundItem, NotFoundQueryResult } from './models';

// eslint-disable-next-line arrow-body-style
const NotFoundPage = () => {
  const {
    currentLang: { locale: currentLangCode },
  } = useCurrentLang();
  const brand = process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string;

  const {
    allContentstackNotFound,
    allContentstackHeader,
    allContentstackProfile,
    allContentstackExtendedHeader,
    allContentstackTranslations,
    allContentstackNewFooter,
    allContentstackFooterWithSplitter,
    allContentstackHeaderWithSplitter,
  } = useStaticQuery<NotFoundQueryResult>(graphql`
    {
      allContentstackHeader {
        nodes {
          ...HeaderBasicFragment
        }
      }
      allContentstackProfile {
        nodes {
          ...ProfileFragment
        }
      }
      allContentstackExtendedHeader {
        nodes {
          ...ExtendedHeaderBasicFragment
        }
      }
      allContentstackTranslations {
        nodes {
          ...Translations
        }
      }
      allContentstackHeaderWithSplitter {
        nodes {
          ...HeaderWithSplitterFragment
        }
      }
      allContentstackFooter {
        nodes {
          ...FooterBasicFragment
        }
      }
      allContentstackNewFooter {
        nodes {
          ...NewFooterBasicFragment
        }
      }
      allContentstackFooterWithSplitter {
        nodes {
          ...FooterWithSplitterFragment
        }
      }
      allContentstackNotFound {
        nodes {
          publishDetails: publish_details {
            locale
          }
          seo {
            ...SeoFragment
          }
          image {
            ...ImageFragment
          }
          button {
            ...LinkFragment
          }
          description {
            ...TypographyFragment
          }
          uid
          tags
          mode
          maxWidth: max_width
          removeAllSpacing: remove_all_spacing
        }
      }
    }
  `);

  const {
    publishDetails: { locale },
    uid,
    image,
    button,
    description,
    seo,
    maxWidth,
    mode,
    removeAllSpacing = true,
  } = allContentstackNotFound.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  ) as NotFoundItem;

  const headerBasic = allContentstackHeader.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const extendedHeaderBasic = allContentstackExtendedHeader.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const translation = allContentstackTranslations.nodes.find(
    (item) => item.publishDetails?.locale === currentLangCode
  );

  const t = mapContentstackTranslations(translation!);

  const headerWithSplitter = allContentstackHeaderWithSplitter.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const footerBasic = allContentstackNewFooter.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const profile = allContentstackProfile.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const footerWithSplitter = allContentstackFooterWithSplitter.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const mappedImage = imageMapper(image);

  return (
    <Layout
      {...{
        seo,
        profile,
        disabledLayoutBackground: true,
        headerBasic: headerBasic && {
          ...headerBasic,
        },
        extendedHeaderBasic: extendedHeaderBasic && {
          ...extendedHeaderBasic,
        },
        translations: t,
        headerWithSplitter,
        headerSplitterAdditionalData: {
          externalData: {},
          currentPageUid: uid,
        },
        footerBasic,
        footerWithSplitter,
        footerSplitterAdditionalData: {
          externalData: {},
          currentPageUid: uid,
        },
        pageUid: uid,
        locale,
        brand,
        className: 'not-found',
      }}
    >
      <Section mode={mode} removeAllSpacing={removeAllSpacing}>
        <Container maxWidth={maxWidth} className="not-found__container">
          {mappedImage?.element ? (
            <ExtendableImage {...(mappedImage as { element: ReactElement })} />
          ) : null}
          <Typography
            {...typographyMapper(description)}
            className="not-found__description"
          />
          <TMPLinkWrap
            {...linkMapper({ ...button, variant: 'primary', size: 'large' })}
            className="not-found__link"
          />
        </Container>
      </Section>
    </Layout>
  );
};

export default NotFoundPage;
